// src/store/slices/inquiriesSlice.js

import { createSlice } from '@reduxjs/toolkit';
import { sendInquiryEmail } from '../thunks/inquiriesActions';

const inquiriesSlice = createSlice({
  name: 'inquiries',
  initialState: {
    list: [],
    current: null,
    loading: false,
    error: null,
  },
  reducers: {
    fetchInquiriesPending:    state => { state.loading = true; state.error = null; },
    fetchInquiriesFulfilled:  (state, { payload }) => { state.loading = false; state.list = payload; },
    fetchInquiriesRejected:   (state, { payload }) => { state.loading = false; state.error = payload; },

    fetchInquiryPending:      state => { state.loading = true; state.error = null; },
    fetchInquiryFulfilled:    (state, { payload }) => { state.loading = false; state.current = payload; },
    fetchInquiryRejected:     (state, { payload }) => { state.loading = false; state.error = payload; },

    createInquiryPending:     state => { state.loading = true; state.error = null; },
    createInquiryFulfilled:   (state, { payload }) => { state.loading = false; state.list.unshift(payload); },
    createInquiryRejected:    (state, { payload }) => { state.loading = false; state.error = payload; },

    replyInquiryPending:      state => { state.loading = true; state.error = null; },
    replyInquiryFulfilled:    (state, { payload }) => {
                                 state.loading = false;
                                 // update current and list
                                 state.current = payload;
                                 state.list = state.list.map(i => i._id === payload._id ? payload : i);
                               },
    replyInquiryRejected:     (state, { payload }) => { state.loading = false; state.error = payload; },

    updateInquiryPending:     state => { state.loading = true; state.error = null; },
    updateInquiryFulfilled:   (state, { payload }) => {
                                 state.loading = false;
                                 state.current = payload;
                                 state.list = state.list.map(i => i._id === payload._id ? payload : i);
                               },
    updateInquiryRejected:    (state, { payload }) => { state.loading = false; state.error = payload; },

    // Local optimistic patch used by the modal right before the server confirms.
    // Keeps the list feeling instant (reply sent -> it pops into the Replied
    // bucket without waiting for the next fetch).
    patchInquiryLocal: (state, { payload }) => {
      const { id, changes = {} } = payload || {};
      if (!id) return;
      state.list = state.list.map((i) => (i._id === id ? { ...i, ...changes } : i));
      if (state.current && state.current._id === id) {
        state.current = { ...state.current, ...changes };
      }
    },
  },
  extraReducers: (builder) => {
    // When the Gmail send call succeeds, the backend returns the fully updated
    // inquiry (including `messages[]` and `responses[]`). Store that object so
    // the conversation history updates immediately in the open modal.
    builder.addCase(sendInquiryEmail.fulfilled, (state, { payload, meta }) => {
      const id = meta?.arg?.id;
      if (!id) return;
      const updatedInquiry = payload?.inquiry;
      if (updatedInquiry?._id) {
        state.list = state.list.map((i) => (i._id === updatedInquiry._id ? updatedInquiry : i));
        if (state.current && state.current._id === updatedInquiry._id) {
          state.current = updatedInquiry;
        }
        return;
      }

      // Legacy fallback for older backend responses that only returned a
      // message record.
      const now = new Date().toISOString();
      state.list = state.list.map((i) =>
        i._id === id
          ? {
              ...i,
              replied: true,
              respondedAt: i.respondedAt || now,
              updatedAt: now,
              messages: payload?.messageRecord
                ? [...(i.messages || []), payload.messageRecord]
                : i.messages,
            }
          : i
      );
      if (state.current && state.current._id === id) {
        state.current = {
          ...state.current,
          replied: true,
          respondedAt: state.current.respondedAt || now,
          updatedAt: now,
          messages: payload?.messageRecord
            ? [...(state.current.messages || []), payload.messageRecord]
            : state.current.messages,
        };
      }
    });
  },
});

export const {
  fetchInquiriesPending,
  fetchInquiriesFulfilled,
  fetchInquiriesRejected,
  fetchInquiryPending,
  fetchInquiryFulfilled,
  fetchInquiryRejected,
  createInquiryPending,
  createInquiryFulfilled,
  createInquiryRejected,
  replyInquiryPending,
  replyInquiryFulfilled,
  replyInquiryRejected,
  updateInquiryPending,
  updateInquiryFulfilled,
  updateInquiryRejected,
  patchInquiryLocal,
} = inquiriesSlice.actions;

export default inquiriesSlice.reducer;
